/**
 * Hero - Flight Card with Clean Design
 * 
 * Displays: Flight # | Route | Departure Timer
 * One glance tells you everything you need
 */

import React, { useEffect, useState } from 'react';
import './hero.css';

export default function Hero({ trip }) {
  const [timeUntilDepart, setTimeUntilDepart] = useState(null);
  const [status, setStatus] = useState('upcoming');

  useEffect(() => {
    if (!trip?.departure_scheduled) return;

    const updateTimer = () => {
      const now = new Date();
      const depart = new Date(trip.departure_scheduled);
      const diff = depart - now;

      if (diff < 0) {
        setStatus('departed');
        setTimeUntilDepart('In Flight');
      } else if (diff < 3600000) {
        // Less than 1 hour
        setStatus('boarding');
        const mins = Math.floor(diff / 60000);
        setTimeUntilDepart(`${mins}m`);
      } else if (diff < 86400000) {
        // Less than 24 hours
        setStatus('soon');
        const hours = Math.floor(diff / 3600000);
        const mins = Math.floor((diff % 3600000) / 60000);
        setTimeUntilDepart(`${hours}h ${mins}m`);
      } else {
        setStatus('future');
        const days = Math.floor(diff / 86400000);
        setTimeUntilDepart(`${days}d away`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [trip]);

  if (!trip) return null;

  return (
    <div className={`hero hero--${status}`}>
      {/* Top Row: Status Badge + Flight Number */}
      <div className="hero__header">
        <div className="hero__status-badge" data-status={status}>
          {status === 'boarding' && '🔴 Boarding Soon'}
          {status === 'departed' && '🟢 In Flight'}
          {status === 'soon' && '🟡 Today'}
          {status === 'future' && '⚫ Upcoming'}
        </div>
        <div className="hero__flight-number">{trip.flight_number}</div>
      </div>

      {/* Route Line: DEP → ARR */}
      <div className="hero__route">
        <div className="route-segment">
          <div className="airport-code">{trip.departure_airport}</div>
          <div className="airport-name">{trip.departure_city}</div>
        </div>
        <div className="route-arrow">→</div>
        <div className="route-segment">
          <div className="airport-code">{trip.arrival_airport}</div>
          <div className="airport-name">{trip.arrival_city}</div>
        </div>
      </div>

      {/* Middle: Time Info */}
      <div className="hero__times">
        <div className="time-block">
          <div className="time-label">Departs</div>
          <div className="time-value">
            {new Date(trip.departure_scheduled).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
        <div className="time-block">
          <div className="time-label">Arrives</div>
          <div className="time-value">
            {new Date(trip.arrival_scheduled).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
        <div className="time-block">
          <div className="time-label">Block</div>
          <div className="time-value">{trip.block_hours?.toFixed(1)}h</div>
        </div>
      </div>

      {/* Bottom: Big Timer */}
      <div className="hero__timer">
        <div className="timer-label">
          {status === 'departed' ? 'Flight In Progress' : 'Time Until Departure'}
        </div>
        <div className="timer-value" data-status={status}>
          {timeUntilDepart}
        </div>
      </div>

      {/* Subtle Footer: Equipment + Tail */}
      <div className="hero__footer">
        <div className="footer-item">
          <span className="footer-label">Aircraft:</span>
          <span className="footer-value">{trip.aircraft_type}</span>
        </div>
        {trip.aircraft_tail && (
          <div className="footer-item">
            <span className="footer-label">Tail:</span>
            <span className="footer-value">{trip.aircraft_tail}</span>
          </div>
        )}
      </div>
    </div>
  );
}
